/**
 * Tenant identity — the non-React core (overhaul Wave 2).
 *
 * Moved out of src/hooks/useTenantId.ts so SERVICES stop importing from the
 * hooks layer (the old inversion had 57 service files depending on a React
 * hook module). The dependency direction is now:
 *
 *   components → hooks → services → lib
 *
 * `src/lib/` imports nothing above it — no React, no services, no hooks.
 * The `useTenantId()` hook still exists in src/hooks/useTenantId.ts as a
 * thin reactive wrapper over this module.
 *
 * Resolution order (unchanged from the original implementation):
 *   1. Firebase Auth token claim (synced into localStorage by the hook)
 *   2. localStorage `tenant_id` (PIN-based auth sessions)
 *   3. DEFAULT_TENANT_ID
 */

// The alias map + read union are SHARED with Cloud Functions (a server copy
// would drift on the next tenant rename). Re-exported here so the hundreds of
// existing `@/lib/tenant` importers are unaffected.
export {
  DEFAULT_TENANT_ID,
  tenantIdReadUnion,
  docBelongsToTenant,
} from '@shared/domain/tenant';
import { DEFAULT_TENANT_ID } from '@shared/domain/tenant';
import { getSession } from './session';

/**
 * Synchronous tenant-ID accessor for non-hook contexts (services, pure
 * helpers). Reads the localStorage value the auth listener keeps in sync;
 * falls back to the platform default before login resolves.
 *
 * Browser-only — stays here rather than in shared/ (no localStorage there).
 */
export function getTenantIdSync(): string {
  // Node-safe (tests, SSR-ish tooling): no localStorage → platform default.
  if (typeof localStorage === 'undefined') return DEFAULT_TENANT_ID;
  return localStorage.getItem('tenant_id') || DEFAULT_TENANT_ID;
}

/**
 * Pre-render tenant selection via URL — `/?tenant=acme-gates`.
 *
 * Until per-tenant domains exist, every tenant shares one app URL, and
 * everything pre-auth (branding, the login picker, getTenantConfig-free
 * tenant doc read) keys off `getTenantIdSync()`, which defaults to the
 * platform default tenant. This gives a second tenant's people a link that
 * lands them on THEIR login: the param is validated as a slug, persisted to
 * localStorage, and stripped from the URL. Call once from main.tsx BEFORE
 * React renders so the first paint is already the right tenant.
 *
 * Signing in normally overwrites `tenant_id` with the user's own tenant
 * (LandingPage.finalizeSignIn), so a stale or mistyped param cannot outlive
 * a real login.
 */
export function applyTenantQueryParam(): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  const param = new URLSearchParams(window.location.search).get('tenant');
  if (!param) return;

  const url = new URL(window.location.href);
  url.searchParams.delete('tenant');
  const stripParam = () => window.history.replaceState(null, '', url.toString());

  // PRE-AUTH ONLY. If a session is already persisted, honouring the param
  // would repoint TenantProvider at another tenant for the rest of the page
  // life — wrong branding, wrong tenant doc, and a poisoned
  // `tenant_feature_packs` cache that hasFeatureSync() reads when deciding
  // server-side-effect behaviour. TenantProvider resolves the tenant exactly
  // once per load and same-tab writes fire no storage event, so there is no
  // recovery short of a reload. Firebase restores auth asynchronously and
  // this runs before React mounts, so check the PERSISTED session rather
  // than auth.currentUser (which is always null this early).
  const session = getSession();
  if (session.userId || session.techId) {
    stripParam();
    return;
  }

  const slug = param.toLowerCase();
  if (!/^[a-z0-9][a-z0-9-]{1,40}$/.test(slug)) {
    stripParam();
    return;
  }
  localStorage.setItem('tenant_id', slug);
  stripParam();
}
